import { type ElementType, type HTMLAttributes, type ReactNode } from "react";
import styled, { css, type DefaultTheme } from "styled-components";

import { devWarn } from "../../utils/devWarning";

export type ElevatedSurfaceElevation = 0 | 1 | 2 | 3 | 4;

export type ElevatedSurfaceBackground =
  | "canvas"
  | "surface"
  | "surfaceSunken"
  | "surfaceRaised"
  | "surfaceRaisedHigh"
  | "surfaceOverlay";

const LEGACY_BACKGROUND = {
  surfaceContainerLowest: "surface",
  surfaceContainerLow: "surfaceSunken",
  surfaceContainer: "surfaceRaised",
  surfaceContainerHigh: "surfaceRaisedHigh",
  surfaceContainerHighest: "surfaceOverlay",
} satisfies Record<string, ElevatedSurfaceBackground>;

/**
 * @deprecated sigmakit legacy surface name. Use `ElevatedSurfaceBackground`.
 */
export type ElevatedSurfaceLegacyBackground = keyof typeof LEGACY_BACKGROUND;

function isLegacyBackground(
  bg: ElevatedSurfaceBackground | ElevatedSurfaceLegacyBackground,
): bg is ElevatedSurfaceLegacyBackground {
  return Object.prototype.hasOwnProperty.call(LEGACY_BACKGROUND, bg);
}

// 같은 legacy 이름이 여러 ElevatedSurface 인스턴스에서 들어와도 한 번만 경고 (key별 글로벌 dedup).
const warnedLegacyBackgrounds = new Set<ElevatedSurfaceLegacyBackground>();

function resolveBackground(
  bg: ElevatedSurfaceBackground | ElevatedSurfaceLegacyBackground,
): ElevatedSurfaceBackground {
  if (isLegacyBackground(bg)) {
    const semantic = LEGACY_BACKGROUND[bg];
    if (!warnedLegacyBackgrounds.has(bg)) {
      warnedLegacyBackgrounds.add(bg);
      devWarn(
        `[c6] <ElevatedSurface background="${bg}"> uses a deprecated legacy name; use "${semantic}".`,
      );
    }
    return semantic;
  }
  return bg;
}

// 레벨 톤은 bg 사다리(sunken < canvas < surface < raised < raisedHigh < overlay)와 단조 정합.
// 1은 surface — 종전 legacy 매핑(1→surfaceSunken)은 다크 사다리 재정렬(sunken이 canvas 아래로)
// 이후 "level 1이 level 0보다 어두운" 모순을 만들어 교정함. sunken은 elevation 축이 아니라
// 파임 표현이라 레벨 매핑에 포함하지 않는다. hover 톤은 다음 레벨(ELEVATION_BG[level+1]).
// export는 테스트 가드용(레벨 톤 단조성 검증) — 패키지 public 표면(index.ts)에는 노출하지 않는다.
export const ELEVATION_BG: Record<ElevatedSurfaceElevation, ElevatedSurfaceBackground> = {
  0: "canvas",
  1: "surface",
  2: "surfaceRaised",
  3: "surfaceRaisedHigh",
  4: "surfaceOverlay",
};

// 그림자는 theme.elevation.shadow.level 토큰 소비 — elevation 단계와 1:1, hover는 다음
// 레벨(level = elevation + 1). in-flow 표면이라 타이트한 지오메트리 + 농도 상승 계열만
// 쓴다. floating 전용 shadow.overlay(popover/dropdown)/shadow.modal(scrim 동반 다이얼로그)은
// ElevatedSurface에서 쓰지 않는다 — scrim 없는 면에 얹으면 라이트에서 퍼져 보인다(RFC-semantic-token-map).
type SurfaceShadowLevel = Exclude<ElevatedSurfaceElevation, 0>;

// hairline은 1px inset ring으로 합성 — border와 달리 레이아웃에 영향이 없고 라이트에선
// transparent라 no-op.
const surfaceShadow = (theme: DefaultTheme, level: SurfaceShadowLevel) =>
  `inset 0 0 0 1px ${theme.elevation.hairline}, ${theme.elevation.shadow.level[level]}`;

const StyledElevatedSurface = styled.div<{
  $elevation: ElevatedSurfaceElevation;
  $hoverElevation: boolean;
  $borderRadius?: string;
  $bg: ElevatedSurfaceBackground;
  $hoverBg: ElevatedSurfaceBackground;
}>`
  position: relative;
  background-color: ${({ theme, $bg }) => theme.color.bg[$bg]};
  color: ${({ theme }) => theme.color.text.default};
  border-radius: ${({ $borderRadius, theme }) => $borderRadius ?? theme.shape.radius.surface};
  box-shadow: ${({ theme, $elevation }) =>
    $elevation === 0 ? "none" : surfaceShadow(theme, $elevation as SurfaceShadowLevel)};
  transition:
    box-shadow 0.3s ease,
    background-color 0.3s ease;
  ${({ $elevation, $hoverElevation, theme, $hoverBg }) =>
    $hoverElevation && $elevation > 0 && $elevation < 4
      ? css`
          &:hover {
            box-shadow: ${surfaceShadow(theme, ($elevation + 1) as SurfaceShadowLevel)};
            background-color: ${theme.color.bg[$hoverBg]};
          }
        `
      : ""}
`;

export interface ElevatedSurfaceProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** elevation 단계 0~4. 기본 1. 4는 최상단(윈도우/다이얼로그 급 표면) — hover 승급 없음. */
  elevation?: ElevatedSurfaceElevation;
  /** hover 시 elevation 한 단계 강조(1~3만). 기본 true. */
  hoverElevation?: boolean;
  /** 모서리 둥글기. 기본 theme.shape.radius.surface. */
  borderRadius?: string;
  /**
   * 배경 surface 토큰. 명시하면 elevation-derived tone과 hover tone bump를 override한다.
   * sigmakit legacy 이름도 deprecated alias로 받는다.
   */
  background?: ElevatedSurfaceBackground | ElevatedSurfaceLegacyBackground;
  /** polymorphic element. 기본 `div`. */
  as?: ElementType;
}

/**
 * Tonal elevation 컨테이너 surface (Material 3 패턴).
 *
 * `elevation`은 surface tone과 shadow를 함께 정하고, `background`가 있으면 tone을 override한다.
 */
export function ElevatedSurface({
  children,
  elevation = 1,
  hoverElevation = true,
  borderRadius,
  background,
  as,
  ...rest
}: ElevatedSurfaceProps) {
  const bg = background !== undefined ? resolveBackground(background) : ELEVATION_BG[elevation];
  // hover 톤 = 다음 레벨의 resting 톤 (shadow hover가 level+1인 것과 동일 규칙).
  const hoverBg =
    background !== undefined || elevation === 0 || elevation === 4
      ? bg
      : ELEVATION_BG[(elevation + 1) as ElevatedSurfaceElevation];

  return (
    <StyledElevatedSurface
      as={as}
      $elevation={elevation}
      $hoverElevation={hoverElevation}
      $borderRadius={borderRadius}
      $bg={bg}
      $hoverBg={hoverBg}
      {...rest}
    >
      {children}
    </StyledElevatedSurface>
  );
}

ElevatedSurface.displayName = "ElevatedSurface";
